import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  CircularProgress,
  Box,
  makeStyles,
  TextField
} from "@material-ui/core";
import clsx from "clsx";
import MarkdownWrapper from "../MarkdownWrapper";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  messagesContainer: {
    maxHeight: 360,
    overflowY: "auto",
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "#2c2c2c" : "#f5f5f5",
  },
  messageRow: {
    display: "flex",
    marginBottom: theme.spacing(1.25),
  },
  messageRowSystem: {
    justifyContent: "center",
  },
  messageRowLeft: {
    justifyContent: "flex-start",
  },
  messageRowRight: {
    justifyContent: "flex-end",
  },
  messageBubble: {
    width: "fit-content",
    maxWidth: "82%",
    minWidth: 120,
    borderRadius: 8,
    padding: theme.spacing(1, 1.25),
    boxShadow: "0 1px 1px rgba(0,0,0,0.2)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: theme.palette.type === "dark" ? "#1f1f1f" : "#303030",
  },
  messageBubbleLeft: {
    backgroundColor: "#ffffff",
    // Fundo claro fixo nos dois temas: o link sem classe volta ao azul do
    // navegador (a regra global do App.js o deixaria branco no modo escuro).
    "& a:not([class])": { color: "revert", textDecoration: "revert" },
    borderTopLeftRadius: 2,
  },
  messageBubbleRight: {
    backgroundColor: "#dcf8c6",
    // Fundo claro fixo nos dois temas: o link sem classe volta ao azul do
    // navegador (a regra global do App.js o deixaria branco no modo escuro).
    "& a:not([class])": { color: "revert", textDecoration: "revert" },
    borderTopRightRadius: 2,
  },
  messageBubbleSystem: {
    backgroundColor: theme.palette.type === "dark" ? "#3b3b3b" : "#eceff1",
    borderRadius: 6,
    color: theme.palette.type === "dark" ? "#f1f1f1" : "#2f2f2f",
    maxWidth: "92%",
    minWidth: 0,
  },
  messageMeta: {
    display: "block",
    marginBottom: theme.spacing(0.5),
    color: theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.74)" : theme.palette.text.secondary,
    fontWeight: 500,
  },
  messageMetaSystem: {
    color: theme.palette.type === "dark" ? "#ffffff" : theme.palette.text.secondary,
  },
}));

const SolicitacaoDesenvolvimentoPreviewModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  previewData
}) => {
  const classes = useStyles();
  const ultimasMensagens = previewData?.ultimasMensagens || [];
  const mensagensOrdenadas = [...ultimasMensagens].reverse();
  const [observacao, setObservacao] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setObservacao(previewData?.observacao || "");
  }, [open, previewData]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{i18n.t("solicitacoesDesenvolvimento.preview.title")}</DialogTitle>
      <DialogContent dividers>
        {!previewData ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="subtitle2">{i18n.t("solicitacoesDesenvolvimento.fields.ticket")}</Typography>
            <Typography variant="body1">#{previewData.ticketId}</Typography>

            <Box mt={2}>
              <Typography variant="subtitle2">{i18n.t("solicitacoesDesenvolvimento.fields.contact")}</Typography>
              <Typography variant="body1">{previewData.contato || i18n.t("common.notInformed")}</Typography>
            </Box>

            <Box mt={2}>
              <Typography variant="subtitle2">{i18n.t("common.phone")}</Typography>
              <Typography variant="body1">{previewData.telefone || i18n.t("common.notInformed")}</Typography>
            </Box>

            <Box mt={2}>
              <Typography variant="subtitle2">{i18n.t("solicitacoesDesenvolvimento.fields.notes")}</Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                variant="outlined"
                value={observacao}
                onChange={(event) => setObservacao(event.target.value)}
                placeholder={i18n.t("solicitacoesDesenvolvimento.preview.notesPlaceholder")}
                disabled={loading}
              />
            </Box>

            <Box mt={3} mb={1}>
              <Typography variant="subtitle2">{i18n.t("solicitacoesDesenvolvimento.preview.lastMessages")}</Typography>
            </Box>

            <Divider />

            <Box mt={2}>
              {ultimasMensagens.length === 0 ? (
                <Typography variant="body2">{i18n.t("solicitacoesDesenvolvimento.preview.noMessages")}</Typography>
              ) : (
                <Box className={classes.messagesContainer}>
                  {mensagensOrdenadas.map((message) => {
                    const fromMe = Boolean(message.fromMe);
                    const systemMessage = Boolean(message?.isSystemMessage);
                    const senderName = String(message?.senderName || "").trim();
                    const senderLabel = systemMessage
                      ? i18n.t("solicitacoesDesenvolvimento.preview.sender.system")
                      : senderName ||
                        (fromMe
                          ? i18n.t("solicitacoesDesenvolvimento.preview.sender.agent")
                          : i18n.t("solicitacoesDesenvolvimento.preview.sender.customer"));
                    const textBody =
                      String(message?.body || "").trim() || i18n.t("solicitacoesDesenvolvimento.preview.media");
                    const messageDate = new Date(message.createdAt).toLocaleString();

                    return (
                      <Box
                        key={message.id}
                        className={clsx(classes.messageRow, {
                          [classes.messageRowSystem]: systemMessage,
                          [classes.messageRowRight]: fromMe && !systemMessage,
                          [classes.messageRowLeft]: !fromMe && !systemMessage,
                        })}
                      >
                        <Box
                          className={clsx(classes.messageBubble, {
                            [classes.messageBubbleSystem]: systemMessage,
                            [classes.messageBubbleRight]: fromMe && !systemMessage,
                            [classes.messageBubbleLeft]: !fromMe && !systemMessage,
                          })}
                        >
                          <Typography
                            variant="caption"
                            className={clsx(classes.messageMeta, {
                              [classes.messageMetaSystem]: systemMessage,
                            })}
                          >
                            {senderLabel} - {messageDate}
                          </Typography>
                          <MarkdownWrapper>{textBody}</MarkdownWrapper>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading} color="default" variant="contained">
          {i18n.t("common.cancel")}
        </Button>
        <Button
          onClick={() => onConfirm(observacao)}
          disabled={loading || !previewData}
          color="primary"
          variant="contained"
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : i18n.t("solicitacoesDesenvolvimento.preview.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SolicitacaoDesenvolvimentoPreviewModal;
